import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, LOGO_URL, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Users, Calendar, TrendingUp, Download, LogOut, Search, Trash2, Loader2, Mail, FileText } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="font-serif-display text-4xl font-bold text-[#0B2C5B]">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, last_7_days: 0, last_30_days: 0 });
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/subscribers/stats"),
        api.get("/admin/subscribers"),
      ]);
      setStats(s.data);
      setSubscribers(list.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const exportCsv = () => {
    const token = localStorage.getItem("vb_token");
    const url = `${API_BASE}/admin/subscribers/export`;
    // Use fetch to send Bearer header, then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `visit-bedford-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      toast.success("Subscriber removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    return subscribers.filter((s) => {
      const matchSearch = !search || s.email.toLowerCase().includes(search.toLowerCase()) || s.source?.toLowerCase().includes(search.toLowerCase());
      const matchDate = !dateFrom || (s.created_at && s.created_at >= dateFrom);
      return matchSearch && matchDate;
    });
  }, [subscribers, search, dateFrom]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard-page">
      {/* Admin header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-site flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src={LOGO_URL} alt="Visit Bedford" className="h-12 w-auto" />
            <div className="hidden sm:block">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">Admin Console</div>
              <div className="font-serif-display text-lg font-bold text-[#0B2C5B] leading-none">Visit Bedford</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-600">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 transition"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        {/* Sub nav */}
        <div className="container-site flex items-center gap-2 pb-3">
          <Link to="/admin" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#E6F6FD] text-[#0B2C5B]">
            <Users className="w-4 h-4" /> Subscribers
          </Link>
          <Link to="/admin/articles" data-testid="admin-nav-articles" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <FileText className="w-4 h-4" /> Articles
          </Link>
        </div>
      </header>

      <main className="container-site py-10 md:py-14">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-2">Subscriber CRM</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] leading-tight">
              Your audience at a glance.
            </h1>
          </div>
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv-btn"
            className="btn-primary-gradient text-white rounded-full px-6 py-3 font-semibold shadow-md hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 self-start md:self-auto"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10" data-testid="admin-stats-grid">
          <StatCard icon={Users} label="Total Subscribers" value={stats.total} accent="#0B2C5B" />
          <StatCard icon={TrendingUp} label="Last 7 Days" value={stats.last_7_days} accent="#FF8C00" />
          <StatCard icon={Calendar} label="Last 30 Days" value={stats.last_30_days} accent="#2AB7E8" />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 flex items-center gap-3 rounded-full px-5 py-3 bg-white border border-slate-200">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or source..."
              data-testid="admin-search-input"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-3 rounded-full px-5 py-3 bg-white border border-slate-200">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="admin-date-from-input"
              className="bg-transparent outline-none text-sm"
            />
            <span className="text-xs text-slate-400">from</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="admin-subscribers-table">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading subscribers…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <Mail className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold text-[#0B2C5B] mb-1">No subscribers yet</p>
              <p className="text-sm">Share your site to start building your list.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold">Email</th>
                    <th className="text-left px-6 py-4 font-semibold">Source</th>
                    <th className="text-left px-6 py-4 font-semibold">Joined</th>
                    <th className="text-right px-6 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-4 font-medium text-[#0B2C5B]">{s.email}</td>
                      <td className="px-6 py-4 text-slate-600">
                        <span className="inline-block px-2.5 py-1 text-xs rounded-full bg-[#E6F6FD] text-[#0B2C5B] font-medium">
                          {s.source || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => remove(s.id)}
                          data-testid={`delete-sub-${s.id}`}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-500">
            Showing {filtered.length} of {subscribers.length} subscribers
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
